import * as R from "ramda";

import { arrayOf, bool, number, shape, string } from "prop-types";
import {
  createFacetString,
  createPageString,
  initFacetsFromQs,
  updateFacets,
  updateQueryParams,
  updateResponse,
} from "../../common/helpers.js";

import { useEffect, useRef, useState } from "react";
import cx from "classnames";

import { mockApiResponse } from "./mockData.js";

import DropdownFacets from "../DropdownFacets/DropdownFacets.jsx";
import Loader from "../Loader/Loader.jsx";
import useMatchMedia from "../../common/useMatchMedia.js";
import { SearchBar } from "./SearchBar.jsx";
import { SelectedFacets } from "./SelectedFacets.jsx";
import ExecEdProgramCard from "../ExecEdProgramCard/ExecEdProgramCard.jsx";
import ContentCard from "./ContentCard.jsx";

const propTypes = {
  storybook: bool,
  index: number,
  dataModel: shape({
    url: string.isRequired,
    header: string,
    perPage: number.isRequired,
    loadMore: bool.isRequired,
    loadMoreText: string.isRequired,
    includeSearchBar: bool.isRequired,
    searchPlaceholder: string,
    sortByLabel: string,
    sorters: arrayOf(shape),
    showFacetsLabel: string.isRequired,
    clearAllLabel: string.isRequired,
    facetKeys: arrayOf(string),
  }).isRequired,
  dataQuery: shape({
    pageId: string.isRequired,
    listingId: string.isRequired,
  }).isRequired,
};

const ProgramListing = (props) => {
  const {
    storybook,
    dataModel: {
      url,
      header,
      loadMore,
      loadMoreText,
      includeSearchBar,
      searchPlaceholder,
      sortByLabel,
      sorters,
      clearAllLabel,
      facetKeys,
      ad1,
      ad2,
    },
    dataQuery: { pageId, listingId },
  } = props;

  // Media queries
  const matchesLarge = useMatchMedia("(min-width: 1200px)");

  // States
  // Store scroll position when clicking CTA button;
  // will be used to return page to proper Y-position after reload
  const [scrollPosition, setScrollPosition] = useState(window.scrollY);

  // Used to show and hide mobile facets full-screen takeover menu
  const [showMobileFacets, setShowMobileFacets] = useState(false);

  // Used to keep track of entered search term
  const [searchTerm, setSearchTerm] = useState("");

  // Keep track of loading state and API response
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState({});

  // Used to keep track of selected/active facets
  const [activeFacets, setActiveFacets] = useState({
    ...initFacetsFromQs(facetKeys),
    page: 1,
  });

  // Keep track of grid vs. list view
  const [view, setView] = useState("grid");

  // Refs
  const searchRef = useRef(null);
  const facetStringRef = useRef();
  const loadedResultsRef = useRef(0);

  // Functions
  const handlePageChange = (page) => {
    setActiveFacets((prev) => R.evolve({ ["page"]: () => page }, prev));
  };

  const handleFacetClick = (facetId, valueId) => {
    setActiveFacets((prev) => {
      // Remove a previously selected sorter,
      // since only one should be active at a time
      if (facetId === "sortBy") {
        prev = R.omit(["sortBy"], prev);
      }

      return updateFacets(facetId, valueId, prev);
    });
  };

  const clearAllFacets = () => {
    setActiveFacets({ page: 1 });
  };

  // Fetch results
  useEffect(() => {
    setIsLoading(true);
    const searchString = searchTerm ? "&q=" + searchTerm : "";
    const facetString = createFacetString(activeFacets) + searchString;
    const pageString = createPageString(activeFacets);

    if (storybook === true) {
      // Pull the results from our mock API data
      const apiResponse = mockApiResponse;

      // If there are ads present,
      // place them in their respective positions in the results array.
      // (The requirement was to put ad1 in the 6th spot in the list,
      // and put ad2 in the 13th spot.)

      // For some reason, response.request is undefined on page load in Storybook.
      // We only want to show the ads on the first page, so I was trying to access response.request.page to do that
      // ...but response.request.page is only available upon loading more items. Is this on purpose?
      // This feels icky but leaving it, at least for now

      // if (ad1 && response.request === undefined) {
      // apiResponse.results.splice(5, 0, ad1);
      // initialResults = initialResults - 1;
      // }

      // if (ad2 && response.request === undefined) {
      // ad2 && apiResponse.results.splice(12, 0, ad2);
      // initialResults = initialResults - 1;
      // }

      setResponse((prevState) =>
        R.mergeDeepWithKey(
          (k, l, r) => (k === "results" ? R.concat(l, r) : r),
          prevState,
          apiResponse,
        ),
      );

      facetStringRef.current = facetString;
      updateQueryParams(facetKeys, activeFacets);
      setIsLoading(false);
    } else {
      // Fetch the results from the API
      fetch(
        `${url}?listingId=${listingId}&pageId=${pageId}${
          facetString ? facetString : ""
        }&${pageString}&${searchString}`,
      )
        .then((response) => response.json())
        .then((data) => {
          // Update the amount of non-ad results loaded so far;
          // needed for hideLoadMore logic
          loadedResultsRef.current = loadedResultsRef.current +=
            data.results.length;

          // Check if ads exist, then insert them.
          if (ad1?.title && data.results.length >= 5) {
            data.results.splice(5, 0, ad1);
          }

          if (ad2?.title && data.results.length >= 12) {
            data.results.splice(12, 0, ad2);
          }

          setResponse((prevState) =>
            updateResponse(facetString, facetStringRef, data, prevState),
          );
        })
        .catch((e) => console.warn(e))
        .finally(() => {
          facetStringRef.current = facetString;
          // Adding 'true' to updateQueryParams to include sorter in URL
          updateQueryParams(facetKeys, activeFacets, null, null, true);
          setIsLoading(false);
        });
    }
  }, [
    url,
    searchTerm,
    listingId,
    pageId,
    loadMore,
    activeFacets,
    facetKeys,
    storybook,
    ad1,
    ad2,
  ]);

  // Hides load more button under table when there's no more results
  const hideLoadMore =
    Object.keys(response).length === 0 ||
    loadedResultsRef.current === response?.totalResults;

  // Handles when users press the enter key in the search box
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchRef.current.value !== searchTerm
        ? [clearAllFacets(), setSearchTerm(searchRef.current.value)]
        : null;
    }
  };

  return (
    <section className="program-listing">
      <h2 className="program-listing__header">{header}</h2>
      <div className="dropdown-facets">
        {matchesLarge ? (
          // Render desktop version of facet dropdown menus
          <div className="dropdown-facets__left">
            {response?.facets &&
              response?.facets.map((facet, i) => {
                // Putting each facet object in an array to easily work with
                // pre-existing functions in the DropdownFacets component
                const arr = [];
                arr.push(facet);

                return (
                  <DropdownFacets
                    key={i}
                    facets={arr}
                    onFacetClick={handleFacetClick}
                    showFacetsLabel={facet.label}
                    clearAllFacets={clearAllFacets}
                    showDownCaret
                  />
                );
              })}

            {/* Retrofitting Sorters into DropdownFacets since same
                  styling and most behaviors are re-used */}
            {sorters && sorters?.length > 0 && (
              <DropdownFacets
                isSorter
                facets={sorters}
                sorters={sorters}
                showSortByLabel={sortByLabel}
                onFacetClick={handleFacetClick}
                clearAllFacets={clearAllFacets}
                showDownCaret
              />
            )}
          </div>
        ) : (
          // Render mobile version of facet dropdown menu
          response?.facets && (
            <DropdownFacets
              facets={response.facets}
              facetMenuOpen={showMobileFacets}
              setFacetMenuOpen={setShowMobileFacets}
              onFacetClick={handleFacetClick}
              clearAllFacets={clearAllFacets}
              showFacetsLabel="Filters"
              sorters={sorters}
              showSortByLabel={sortByLabel}
              clearAllLabel={clearAllLabel}
              totalResults={response?.totalResults}
              showDownCaret={false}
            />
          )
        )}

        {/* Change order of facet bubbles and search bar
            depending on monitor width */}
        {matchesLarge ? (
          <>
            <SearchBar
              includeSearchBar={includeSearchBar}
              searchPlaceholder={searchPlaceholder}
              searchRef={searchRef}
              handleKeyDown={handleKeyDown}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchIcon={searchIcon}
              clearAllFacets={clearAllFacets}
            />
          </>
        ) : (
          <>
            <SelectedFacets
              clearAllLabel={clearAllLabel}
              clearAllFacets={clearAllFacets}
              handleFacetClick={handleFacetClick}
              response={response}
              setShowMobileFacets={setShowMobileFacets}
              showMobileFacets={showMobileFacets}
              activeFacets={activeFacets}
              view={view}
              setView={setView}
            />
            <SearchBar
              includeSearchBar={includeSearchBar}
              searchPlaceholder={searchPlaceholder}
              searchRef={searchRef}
              handleKeyDown={handleKeyDown}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              searchIcon={searchIcon}
              clearAllFacets={clearAllFacets}
            />
          </>
        )}
      </div>
      {matchesLarge && (
        <SelectedFacets
          clearAllLabel={clearAllLabel}
          clearAllFacets={clearAllFacets}
          handleFacetClick={handleFacetClick}
          response={response}
          setShowMobileFacets={setShowMobileFacets}
          showMobileFacets={showMobileFacets}
          activeFacets={activeFacets}
          view={view}
          setView={setView}
        />
      )}

      {(() => {
        if (response.results?.length > 0) {
          return (
            <div
              className={cx("program-listing__cards", {
                "program-listing__cards--grid": view === "grid" && matchesLarge,
                "program-listing__cards--list":
                  view === "list" || !matchesLarge,
              })}
            >
              {response.results?.map((result) => {
                switch (result.type) {
                  case "ad":
                    return (
                      <ContentCard view={view} {...result} key={result.key} />
                    );
                  case "program":
                    return (
                      <ExecEdProgramCard
                        view={view}
                        {...result}
                        key={result.key}
                        equalHeights={false}
                      />
                    );
                  default:
                    console.warn(
                      "Result(s) were found without type 'ad' or 'program'. Make sure result items have a 'type'.",
                    );
                    return;
                }
              })}
            </div>
          );
        } else if (!isLoading && !response.results?.length) {
          // Render the message below when there are no results, and the page is done loading
          return <p>No results found.</p>;
        } else {
          return;
        }
      })()}

      {isLoading && <Loader />}

      <div
        className={cx("program-listing__cta", {
          "program-listing__cta--hidden": hideLoadMore,
        })}
      >
        <button
          className="call-to-action call-to-action--outline"
          onMouseDown={() => {
            setScrollPosition(window.scrollY);
          }}
          onClick={() => {
            handlePageChange(activeFacets.page + 1);
          }}
          type="button"
        >
          {loadMoreText}
        </button>
      </div>
    </section>
  );
};

// Specifies the default values for props:
ProgramListing.defaultProps = {
  storybook: false,
};

ProgramListing.propTypes = propTypes;
export default ProgramListing;

const searchIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="feather feather-search"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
